import type { Pagination } from "@/models/user.model";

export const OperationStatuses = [
  "Pending",
  "Processing",
  "Completed",
  "Failed",
  "Cancelled",
] as const;

export type OperationStatus = (typeof OperationStatuses)[number];

export type OperationType = "Deposit" | "Withdrawal";

export const ProviderOperationStates = [
  "Available",
  "InProgressByProvider",
  "CompletedByProvider",
  "FailedByProvider",
  "CancelledByProvider",
  "ClosedWithoutProvider",
] as const;

export type ProviderOperationState = (typeof ProviderOperationStates)[number];

export interface Deposit {
  id: string;
  userId?: string;
  userFullName?: string;
  referenceNumber: string;
  amount: number;
  fixedFee?: number;
  percentageFee?: number;
  percentageFeeRate?: number;
  totalFee?: number;
  netAmount?: number;
  status: OperationStatus;
  providerId: string | null;
  providerFullName: string | null;
  takenByProvider?: boolean;
  canBeTakenByProvider?: boolean;
  providerOperationState?: ProviderOperationState;
  transferMethodId: string;
  transferMethodName: string;
  clientId?: string;
  clientFullName?: string;
  processedAt?: string | null;
  completedAt?: string | null;
  failureReason?: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface Withdrawal {
  id: string;
  userId?: string;
  userFullName?: string;
  referenceNumber: string;
  amount: number;
  fixedFee?: number;
  percentageFee?: number;
  percentageFeeRate?: number;
  totalFee?: number;
  netAmount?: number;
  status: OperationStatus;
  providerId: string | null;
  providerFullName: string | null;
  takenByProvider?: boolean;
  canBeTakenByProvider?: boolean;
  providerOperationState?: ProviderOperationState;
  transferMethodId: string;
  transferMethodName: string;
  clientId?: string;
  clientFullName?: string;
  processedAt?: string | null;
  completedAt?: string | null;
  failureReason?: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export type Operation = (Deposit | Withdrawal) & {
  operationType: OperationType;
};

export interface OperationListParams {
  page?: number;
  pageSize?: number;
  referenceNumber?: string;
  status?: OperationStatus;
  transferMethodId?: string;
  providerId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface PaginatedOperations<T> {
  items: T[];
  pagination: Pagination;
}

export interface ConfirmOperationRequest {
  approve: boolean;
  reason: string | null;
}

export interface AdminProcessRequest {
  approve: boolean;
  reason?: string | null;
}

export interface DashboardStatusCounts {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  cancelled: number;
  total: number;
}

export interface ProcessingOperation {
  id: string;
  operationType: OperationType;
  referenceNumber: string;
  providerId: string;
  providerName: string;
  amount: number;
  processedAt: string;
  createdAt: string;
}

export interface DashboardStats {
  deposits: DashboardStatusCounts;
  withdrawals: DashboardStatusCounts;
  processingOperations: ProcessingOperation[];
}

export interface StuckOperation {
  id: string;
  operationType: OperationType;
  referenceNumber: string;
  providerId: string;
  providerName: string;
  amount: number;
  processedAt: string;
  createdAt: string;
}

export interface OperationNote {
  id: string;
  operationId: string;
  authorId: string;
  authorName: string;
  authorRole?: string;
  content: string;
  imageUrl: string | null;
  imageFileId: string | null;
  isViewed: boolean;
  viewedAt: string | null;
  createdAt: string;
}

export interface CreateNoteRequest {
  content?: string;
  image?: File | null;
}

interface DepositNoteApi {
  id: string;
  depositId: string;
  authorId: string;
  authorName?: string;
  authorFullName?: string;
  authorRole?: string;
  content?: string | null;
  imageUrl?: string | null;
  imageFileId?: string | null;
  isViewed: boolean;
  viewedAt: string | null;
  createdAt: string;
}

interface WithdrawalNoteApi {
  id: string;
  withdrawalId: string;
  authorId: string;
  authorFullName?: string;
  authorName?: string;
  authorRole?: string;
  content?: string | null;
  imageUrl?: string | null;
  imageFileId?: string | null;
  isViewed: boolean;
  viewedAt: string | null;
  createdAt: string;
}

export function normalizeDepositNote(note: DepositNoteApi): OperationNote {
  return {
    id: note.id,
    operationId: note.depositId,
    authorId: note.authorId,
    authorName: note.authorName || note.authorFullName || "",
    authorRole: note.authorRole,
    content: note.content ?? "",
    imageUrl: note.imageUrl ?? null,
    imageFileId: note.imageFileId ?? null,
    isViewed: note.isViewed,
    viewedAt: note.viewedAt,
    createdAt: note.createdAt,
  };
}

export function normalizeWithdrawalNote(
  note: WithdrawalNoteApi
): OperationNote {
  return {
    id: note.id,
    operationId: note.withdrawalId,
    authorId: note.authorId,
    authorName: note.authorName || note.authorFullName || "",
    authorRole: note.authorRole,
    content: note.content ?? "",
    imageUrl: note.imageUrl ?? null,
    imageFileId: note.imageFileId ?? null,
    isViewed: note.isViewed,
    viewedAt: note.viewedAt,
    createdAt: note.createdAt,
  };
}
